import humps from 'humps'
import { cloneDeep, get, reduce, set } from 'lodash'
import deserialize from 'app/utils/deserialize'
import { matching } from 'app/releases'
import { fetch } from 'app/utils/data'
import collection_data_v7 from 'app/views/search/collections-v7.json'

function find(permits, type) {
  const yep = get(permits, ['yep', type], null)
  const nope = get(permits, ['nope', type], null)

  return yep ? { yep: true, nope: false, permit: yep } : { yep: false, nope: true, permit: nope }
}

function update(permits, type, updater, iconCollection) {
  const { yep, permit } = find(permits, type)

  if (!permit) return permits

  const nextPermit = updater(permit, iconCollection)

  return yep ? set(permits, ['yep', type], nextPermit) : set(permits, ['nope', type], nextPermit)
}

function transformPermitVersions(permit, iconCollection, _version) {
  permit.versions = permit.versions.map(({ major, minor, patch, pre }) => {
    const release = cloneDeep(
      matching({
        version: `${major}.${minor}.${patch}${pre.length === 1 ? '-' + pre[0] : ''}`
      })
    )

    const uniqueFamilyStyles = new Set()

    release.familyStyles = release.familyStyles.filter((familyStyle) => {
      const matches = iconCollection.iconPacks.some((packName) => {
        const pack = collection_data_v7.iconPacks.find((p) => p.name === packName)
        if (!pack) return false

        // Check if the familyStyle matches any of the familyStyles in the pack
        return pack.familyStyles.some((packStyle) => packStyle.family === familyStyle.family && packStyle.style === familyStyle.style)
      })

      if (matches) {
        // Create a unique key for this familyStyle combination to help avoid duplicates in set
        const familyStyleKey = `${familyStyle.family}-${familyStyle.style}`

        // Only keep the `styleKey` if it is not already in the set
        if (!uniqueFamilyStyles.has(familyStyleKey)) {
          uniqueFamilyStyles.add(familyStyleKey)
          return true
        }
      }
      return false
    })

    // Filter out any family in familyStyleCounts that are not present in release.familyStyles
    release.familyStyleCounts = Object.fromEntries(
      Object.entries(release.familyStyleCounts).filter(([family]) => {
        return release.familyStyles.some((style) => style.family === family)
      })
    )

    return release
  })

  return permit
}

function getIconCollectionForPermit(permitName, _version) {
  const permitNameToIconCollection = {
    access_to_pro: 'pro-collection',
    access_to_pro_download: 'pro-collection',
    access_to_pro_plus: 'pro-plus-collection',
    access_to_pro_plus_download: 'pro-plus-collection'
  }

  const iconCollections = collection_data_v7.iconCollections.find((collection) => {
    return collection.name === permitNameToIconCollection[permitName]
  })

  return iconCollections
}

const decorators = [
  (permits) => {
    const camelize = (grouping) => {
      return reduce(
        grouping,
        (acc, permit, permitKey) => {
          if (permit.flags) {
            permit.flags = permit.flags.map((flag) => {
              return humps.camelize(flag)
            })
          }

          acc[permitKey] = permit

          return acc
        },
        {}
      )
    }

    permits.yep = camelize(permits.yep)
    permits.nope = camelize(permits.nope)

    return permits
  },
  (permits) => {
    const permitMap = {
      accessToPro: 'access_to_pro',
      accessToProDownload: 'access_to_pro_download',
      accessToProPlus: 'access_to_pro_plus',
      accessToProPlusDownload: 'access_to_pro_plus_download'
    }

    let nextPermits = permits
    Object.entries(permitMap).forEach(([key, permitName]) => {
      nextPermits = update(nextPermits, key, transformPermitVersions, getIconCollectionForPermit(permitName))
    })

    return nextPermits
  }
]

export default {
  namespaced: true,

  state: {
    raw: null,
    item: null
  },

  getters: {
    all: (state) => state.item,

    raw: (state) => state.raw,

    find: (state) => (type) => {
      return find(state.item, type)
    },

    has: (state) => (type) => {
      const { yep } = find(state.item, type)

      return yep
    },

    adFreeDocumentationVersions: (state) => get(state.item, ['yep', 'adFreeDocumentation', 'versions'], []),

    accessToPro: (state) => get(state.item, ['yep', 'accessToPro', 'versions'], []).map((v) => v.versionParts),

    accessToProPlus: (state) => get(state.item, ['yep', 'accessToProPlus', 'versions'], []).map((v) => v.versionParts),

    accessToProDownloadVersions: (state) => get(state.item, ['yep', 'accessToProDownload', 'versions'], []).map((v) => v.versionParts)
  },

  mutations: {
    permitsFetched(state, { permits, iconCollections }) {
      const params = {
        iconCollections
      }

      state.raw = permits

      state.item = reduce(
        decorators,
        (acc, decorator) => {
          return decorator(acc, params)
        },
        cloneDeep(permits)
      )
    },

    resetState(state) {
      state.raw = null
      state.item = null
    }
  },

  actions: {
    async fetchPermits({ commit }) {
      try {
        const response = await fetch({
          url: `/api/account/permits`
        })

        const permits = await deserialize(response.data)

        commit('permitsFetched', { permits })

        return permits
      } catch (e) {
        if (get(e, ['response', 'status']) === 404) {
          return null
        } else {
          throw e
        }
      }
    }
  }
}
